import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../App";
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

function bmLabel(key) {
  return BOOKMAKER_LABELS[key] || key;
}

function formatKickoff(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleDateString('en-BW', { weekday: 'short', day: 'numeric', month: 'short' }) +
    ' ' + d.toLocaleTimeString('en-BW', { hour: '2-digit', minute: '2-digit' });
}

export default function Middles() {
  const [middles, setMiddles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [stake, setStake] = useState(100);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [minWin, setMinWin] = useState(0);
  const [sortBy, setSortBy] = useState('bestWin');
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  const fetchMiddles = useCallback(async () => {
    setLoading(true);
    try {
      const [data, sum] = await Promise.all([
        apiFetch(`/middles?limit=100&minWin=${minWin}`),
        apiFetch('/middles/summary'),
      ]);
      setMiddles(data.middles || []);
      setSummary(sum);
      setLastRefresh(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [minWin]);

  useEffect(() => { fetchMiddles(); }, [fetchMiddles]);
  useEffect(() => {
  const t = setInterval(() => {
    if (!document.hidden) fetchMiddles();
  }, 120000);
  
  const handleVisibility = () => {
    if (!document.hidden) fetchMiddles();
  };
  document.addEventListener('visibilitychange', handleVisibility);
  
  return () => {
    clearInterval(t);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}, [fetchMiddles]);

  function calcMiddle(middle, totalStake) {
    const s = totalStake / 2;
    const overReturn = s * middle.overPrice;
    const underReturn = s * middle.underPrice;
    const bothWin = overReturn + underReturn - totalStake;
    const bestSingle = Math.max(overReturn, underReturn) - totalStake;
    return {
      stakeEach: s.toFixed(2),
      bothWinProfit: bothWin.toFixed(2),
      missLoss: Math.abs(bestSingle).toFixed(2),
      ratio: (bothWin / Math.abs(bestSingle)).toFixed(1),
    };
  }

  return (
    <div className="no-select" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Watermark />

      {/* Summary stats — Fix: smaller font on mobile, no truncation */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
        {[
          { label: 'Active Middles', value: loading ? '—' : summary?.total ?? 0, sub: '↑ Updated every 30 min', color: 'var(--accent)' },
          { label: 'Best Win', value: loading ? '—' : summary ? `P${summary.bestWin}` : '—', sub: 'Per P200 if hits', color: 'var(--success)' },
          { label: 'Avg Win', value: loading ? '—' : summary ? `P${summary.avgWin}` : '—', sub: 'Per P200 staked', color: 'var(--accent2)' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ '--accent-color': s.color, textAlign: 'center' }}>
            <div className="stat-label" style={{ textAlign: 'center' }}>{s.label}</div>
            <div className="stat-value" style={{ fontSize: isMobile ? 16 : 24, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.value}</div>
            <div className="stat-change up" style={{ textAlign: 'center', justifyContent: 'center', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Explainer */}
      <div style={{
        background: 'rgba(61,142,248,0.06)', border: '1px solid rgba(61,142,248,0.15)',
        borderRadius: 'var(--radius)', padding: '14px 16px',
        display: 'flex', gap: 12, alignItems: 'flex-start',
      }}>
        <div style={{ fontSize: 20, flexShrink: 0 }}>🎯</div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>How middles work</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
            Bet Over X at one bookmaker and Under Y at another. If the match ends with the right number of goals in the window, <strong style={{ color: 'var(--text2)' }}>both bets win</strong>. If not, one wins and one loses — a small loss.
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' }}>Min Win</label>
            <select value={minWin} onChange={e => setMinWin(parseFloat(e.target.value))} style={{ fontSize: 13 }}>
              <option value={0}>Any</option>
              <option value={50}>P50+</option>
              <option value={100}>P100+</option>
              <option value={150}>P150+</option>
              <option value={200}>P200+</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' }}>Sort By</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ fontSize: 13 }}>
              <option value="bestWin">Best Win</option>
              <option value="smallestMiss">Smallest Miss</option>
              <option value="zeroRisk">Zero Risk Only</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {lastRefresh && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button className="btn btn-outline" style={{ fontSize: 11, padding: '5px 12px' }} onClick={fetchMiddles} disabled={loading}>
              {loading ? '...' : '↺ Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* List + detail */}
      <div style={{ display: 'grid', gridTemplateColumns: selected && !isMobile ? '1fr 360px' : '1fr', gap: 16, alignItems: 'start' }}>
        <div className="card">
          {loading ? (
            <div style={{ padding: 20 }}>
              {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80, marginBottom: 10 }} />)}
            </div>
          ) : middles.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎯</div>
              <p>No middles found right now.</p>
              <p style={{ marginTop: 6, fontSize: 12 }}>Middles appear when bookmakers offer different Over/Under lines.</p>
            </div>
          ) : (() => {
            const sortedMiddles = [...middles]
              .filter(m => {
                const calc = calcMiddle(m, 200);
                if (sortBy === 'zeroRisk') return parseFloat(calc.missLoss) === 0;
                return true;
              })
              .sort((a, b) => {
                const calcA = calcMiddle(a, 200);
                const calcB = calcMiddle(b, 200);
                if (sortBy === 'smallestMiss') return parseFloat(calcA.missLoss) - parseFloat(calcB.missLoss);
                return parseFloat(calcB.bothWinProfit) - parseFloat(calcA.bothWinProfit);
              });

            if (sortedMiddles.length === 0) return (
              <div className="empty-state">
                <div className="empty-icon">🛡️</div>
                <p>No zero risk middles right now.</p>
              </div>
            );

            return sortedMiddles.map(m => {
            const calc = calcMiddle(m, 200);
            const isSelected = selected?._id === m._id;
            return (
              <div
                key={m._id}
                onClick={() => setSelected(s => s?._id === m._id ? null : m)}
                style={{
                  padding: isMobile ? '12px 14px' : '16px 20px',
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                  background: isSelected ? 'var(--bg4)' : 'transparent',
                  transition: 'background .12s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                      ⚽ {m.homeTeam} vs {m.awayTeam}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.league} · {formatKickoff(m.commenceTime)}
                    </div>
                    {/* Legs */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {parseFloat(calc.missLoss) === 0 && (
                        <span className="pill pill-green" style={{ fontSize: 10 }}>🛡️ Zero Risk</span>
                      )}
                      <span className="pill pill-teal" style={{ fontSize: 10 }}>
                        Over {m.overLine} @ {m.overPrice} — {bmLabel(m.overBookmaker)}
                      </span>
                      <span className="pill pill-blue" style={{ fontSize: 10 }}>
                        Under {m.underLine} @ {m.underPrice} — {bmLabel(m.underBookmaker)}
                      </span>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text3)' }}>
                      Window: {m.windowSize === 1
                        ? `exactly ${m.overLine + 0.5} goals`
                        : `${m.overLine + 0.5}–${m.underLine - 0.5} goals`} wins both
                    </div>
                  </div>
                  {/* P&L */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: isMobile ? 15 : 20, color: 'var(--success)' }}>
                      +P{calc.bothWinProfit}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>if hits</div>
                    <div style={{ fontSize: isMobile ? 11 : 12, color: 'var(--danger)', marginTop: 2, fontWeight: 600 }}>
                      -P{calc.missLoss}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>if miss</div>
                  </div>
                </div>
              </div>
            );
            });
          })()}
        </div>

        {/* Calculator — modal on mobile, sidebar on desktop */}
        {selected && (
          isMobile ? (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(8,13,23,0.97)',
              backdropFilter: 'blur(8px)',
              overflow: 'auto', padding: '20px 16px',
            }}>
              <div className="card" style={{ maxWidth: 480, margin: '0 auto' }}>
                <div className="section-header">
                  <div className="section-title">🎯 Middle Calculator</div>
                  <button onClick={() => setSelected(null)} style={{ color: 'var(--text3)', fontSize: 22, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                </div>
                <MiddleCalc middle={selected} stake={stake} setStake={setStake} calcMiddle={calcMiddle} bmLabel={bmLabel} formatKickoff={formatKickoff} />
              </div>
            </div>
          ) : (
            <div className="card" style={{ position: 'sticky', top: 80 }}>
              <div className="section-header">
                <div className="section-title">🎯 Middle Calculator</div>
                <button onClick={() => setSelected(null)} style={{ color: 'var(--text3)', fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
              </div>
              <MiddleCalc middle={selected} stake={stake} setStake={setStake} calcMiddle={calcMiddle} bmLabel={bmLabel} formatKickoff={formatKickoff} />
            </div>
          )
        )}
      </div>
    </div>
  );
}

function MiddleCalc({ middle, stake, setStake, calcMiddle, bmLabel, formatKickoff }) {
  const calc = calcMiddle(middle, stake);

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
          {middle.homeTeam} vs {middle.awayTeam}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{middle.league} · {formatKickoff(middle.commenceTime)}</div>
      </div>

      {/* Window */}
      <div style={{ background: 'rgba(0,229,176,0.06)', border: '1px solid rgba(0,229,176,0.2)', borderRadius: 'var(--radius-sm)', padding: 14, textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>Both bets win if exactly</div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, color: 'var(--accent)' }}>
          {middle.windowSize === 1
            ? `${middle.overLine + 0.5} goals`
            : `${middle.overLine + 0.5}–${middle.underLine - 0.5} goals`}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>scored in this match</div>
      </div>

      {/* Stake input — fontSize 16 prevents iOS zoom */}
      <div>
        <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5 }}>
          Total stake (BWP)
        </label>
        <input
          type="number"
          value={stake}
          onChange={e => setStake(parseFloat(e.target.value) || 100)}
          style={{ width: '100%', fontSize: 16, fontFamily: 'var(--font-display)', fontWeight: 700 }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {[100, 200, 500, 1000].map(a => (
            <button key={a} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setStake(a)}>P{a}</button>
          ))}
        </div>
      </div>

      {/* Legs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5 }}>Place these bets:</div>
        {[
          { label: `Over ${middle.overLine}`, bookmaker: middle.overBookmaker, odds: middle.overPrice },
          { label: `Under ${middle.underLine}`, bookmaker: middle.underBookmaker, odds: middle.underPrice },
        ].map((leg, i) => (
          <div key={i} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{leg.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{bmLabel(leg.bookmaker)} · @ {leg.odds}</div>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)', fontSize: 15 }}>
              P{calc.stakeEach}
            </div>
          </div>
        ))}
      </div>

      {/* P&L summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ background: 'rgba(18,201,142,0.08)', border: '1px solid rgba(18,201,142,0.2)', borderRadius: 'var(--radius-sm)', padding: '12px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>✅ If HITS</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: 'var(--success)' }}>+P{calc.bothWinProfit}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>both bets win</div>
        </div>
        <div style={{ background: 'rgba(240,71,71,0.06)', border: '1px solid rgba(240,71,71,0.15)', borderRadius: 'var(--radius-sm)', padding: '12px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>❌ If MISSES</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: 'var(--danger)' }}>-P{calc.missLoss}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>one wins, one loses</div>
        </div>
      </div>

      <div style={{ padding: '10px 12px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
        💡 Ratio: <strong style={{ color: 'var(--text2)' }}>{calc.ratio}x</strong> — for every P1 risked, you gain P{calc.ratio} if the middle hits.
      </div>

      <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
        ⚠️ Place both bets quickly — odds can change fast
      </div>
    </div>
  );
}